import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { useSaveFlow, useConfirm } from "../../components/confirm";
import { useHotkeys, formatCombo } from "../../components/hotkeys";
import { useNavActions } from "../../hooks/useNavActions";
import { FormTitle } from "../../components/FormTitle";
import { AppDialog } from "../../components/AppDialog/AppDialog";
import {
  ColFilterButton,
  passesColumnFilter,
  cascadeRows,
  type ColumnFilterValue,
} from "../../components/ColumnFilterDropdown";
import "./SettingsDetailPage.css"; // reuse the Settings-detail `.sd-*` chrome
import "./SampleFieldConfig.css"; // reuse the `.sfc-*` dual-list styles

/**
 * Settings → Sample Registration → Sample Matrix Field Setup — full-page inline
 * detail (ALPACA `VisualMatrix_DetailView_FieldSetup` parity). DISTINCT from
 * Sample Field Configuration: a SINGLE "Field Configuration" panel (no Station /
 * Sampling Information / Monitoring Records tabs), backed by
 * `dbo.SampleMatrixSetupFields`, whose editable "Selected" grid has exactly the
 * columns Name · Freeze · Width · Order · Caption (no FieldClass / IsMandatory /
 * IsReadonly). Saves through the shared useSaveFlow (§18.6).
 */

interface FieldOption {
  fieldId: string;
  fieldCaption: string;
}
interface FieldRow {
  fieldId: string;
  fieldCaption: string;
  fieldCustomCaption: string | null;
  freeze: boolean;
  width: number;
  sortOrder: number;
}
interface Detail {
  oid: string;
  visualMatrixName: string | null;
  matrixName: string | null;
  visualMatrixCode: string | null;
  selected: FieldRow[];
  available: FieldOption[];
}

// The nine default fields ALPACA refuses to remove
// (SampleMatrixFieldsetupController.RemoveFields_Execute). "VisualMatrix" is
// shown to the user as "SampleMatrix".
const REQUIRED_FIELDS = new Set(
  ["SampleID", "VisualMatrix", "ReceivedDate", "ClientSampleID", "CollectDate",
   "CollectTimeDisplay", "SampleSource", "Hold", "ExcludeInvoice"].map((s) => s.toLowerCase()),
);
const displayField = (f: string) => (f === "VisualMatrix" ? "SampleMatrix" : f);

interface Props {
  id: string | null;
  pageId?: string;
  title?: string;
  group?: string;
  onHome?: () => void;
  onSaved: () => void;
  onClose: () => void;
}

export function SampleMatrixFieldSetupDetail({
  id,
  pageId = "set-sample-matrix-field-setup",
  title,
  group,
  onHome,
  onSaved,
  onClose,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();

  // MAC gate (fail-open for admins / no MAC): a Read-only user views the field
  // setup but cannot Save / Copy Fields / add / remove / edit any cell. Keyed by
  // the Alpaca nav id (pageId = 'set-sample-matrix-field-setup' →
  // VisualMatrix_ListView_FieldSetup). Server-side NavMacGuard re-checks each write.
  const canWrite = useNavActions(pageId).write;

  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | undefined>();
  const [name, setName] = useState("");
  const [matrixName, setMatrixName] = useState("");
  const [selected, setSelected] = useState<FieldRow[]>([]);
  const [available, setAvailable] = useState<FieldOption[]>([]);
  // Excel-style per-column filters (ALPACA header-popup parity) — one map per
  // panel. `null`/absent ⇒ that column's filter is inactive.
  const [availFilters, setAvailFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [selFilters, setSelFilters] = useState<Record<string, ColumnFilterValue>>({});

  // "Copy Fields" (ALPACA Copytofield): copy this matrix's current field set to
  // other matrices, replacing each target's config.
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargets, setCopyTargets] = useState<{ oid: string; sampleMatrix: string; matrix: string }[]>([]);
  const [copyChecked, setCopyChecked] = useState<Set<string>>(new Set());
  const [copyBusy, setCopyBusy] = useState(false);

  const openCopyFields = () => {
    if (!id) return;
    setCopyChecked(new Set());
    setCopyOpen(true);
    api
      .get<{ data?: { oid: string; sampleMatrix: string; matrix: string }[] }>(
        `/api/v1/sample-matrix-field-setup/${id}/target-matrices`,
      )
      .then((res) => setCopyTargets(res.data.data ?? []))
      .catch(() => notify.error("Could not load the matrices to copy to."));
  };

  const toggleCopyTarget = (oid: string) =>
    setCopyChecked((prev) => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid);
      else next.add(oid);
      return next;
    });

  const doCopyFields = async () => {
    if (!canWrite) return;
    if (copyChecked.size === 0) {
      notify.error("Select at least one matrix to copy to.");
      return;
    }
    const targets = copyTargets.filter((t) => copyChecked.has(t.oid)).map((t) => t.sampleMatrix);
    const ok = await confirm.confirm({
      title: "Copy Fields",
      message: `Copy this field configuration to ${copyChecked.size} matrix(es) — ${targets.join(", ")}? Each target's existing configuration will be replaced.`,
    });
    if (!ok) return;
    setCopyBusy(true);
    try {
      await api.post(`/api/v1/sample-matrix-field-setup/${id}/copy-fields`, {
        targetMatrixOids: Array.from(copyChecked),
        fields: selected.map((s) => ({
          fieldId: s.fieldId,
          fieldCaption: s.fieldCaption,
          fieldCustomCaption: s.fieldCustomCaption,
          freeze: s.freeze,
          width: s.width,
          sortOrder: s.sortOrder,
        })),
      });
      notify.success(`Fields copied to ${copyChecked.size} matrix(es) successfully.`);
      setCopyOpen(false);
    } catch {
      notify.error("Could not copy the fields.");
    } finally {
      setCopyBusy(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoading(true);
    const t0 = performance.now();
    api
      .get<{ data?: Detail }>(`/api/v1/sample-matrix-field-setup/${id}`)
      .then((res) => {
        if (!alive) return;
        const d = res.data.data;
        if (d) {
          setName(d.visualMatrixName ?? "");
          setMatrixName(d.matrixName ?? "");
          setSelected(d.selected ?? []);
          setAvailable(d.available ?? []);
        }
        setLoadMs(performance.now() - t0);
      })
      .catch(() => {
        if (alive) notify.error("Could not load the sample matrix field setup.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Drag-and-drop: drag an Available field onto the Selected panel to add it
  // (in addition to the + button) — user request 2026-09-29, ALPACA-style.
  const dragIdRef = useRef<string | null>(null);
  const [dropOver, setDropOver] = useState(false);

  const addField = (opt: FieldOption) => {
    if (!canWrite) return;
    // ALPACA AddFields_Execute defaults: Width = 100, SortOrder = count + 1,
    // Freeze = false, Caption (FieldCustomCaption) = blank.
    setSelected((prev) => {
      if (prev.some((r) => r.fieldId === opt.fieldId)) return prev; // guard double-add
      const nextOrder = prev.reduce((m, r) => Math.max(m, r.sortOrder), 0) + 1;
      return [
        ...prev,
        {
          fieldId: opt.fieldId,
          fieldCaption: opt.fieldCaption,
          fieldCustomCaption: null,
          freeze: false,
          width: 100,
          sortOrder: nextOrder,
        },
      ];
    });
    setAvailable((prev) => prev.filter((a) => a.fieldId !== opt.fieldId));
  };

  /** Add by field id (used by the drag-drop drop handler). */
  const addFieldById = (fid: string | null | undefined) => {
    if (!fid) return;
    const opt = available.find((a) => a.fieldId === fid);
    if (opt) addField(opt);
  };

  const removeField = (row: FieldRow) => {
    if (!canWrite) return;
    // ALPACA blocks removal of the nine default fields (matched by FieldID/Caption).
    if (REQUIRED_FIELDS.has(row.fieldId.toLowerCase()) || REQUIRED_FIELDS.has(row.fieldCaption.toLowerCase())) {
      notify.error(`${displayField(row.fieldCaption || row.fieldId)} is default field cannot be removed.`);
      return;
    }
    setSelected((prev) => prev.filter((r) => r.fieldId !== row.fieldId));
    setAvailable((prev) =>
      [...prev, { fieldId: row.fieldId, fieldCaption: row.fieldCaption }].sort((a, b) =>
        a.fieldCaption.localeCompare(b.fieldCaption),
      ),
    );
  };

  const patchRow = (fieldId: string, patch: Partial<FieldRow>) =>
    setSelected((prev) => prev.map((r) => (r.fieldId === fieldId ? { ...r, ...patch } : r)));

  const doSave = async () => {
    if (!id || !canWrite) return;
    await runSave({
      formName: "Sample Matrix Field Setup",
      auditAction: "edit",
      save: async () => {
        await api.put(`/api/v1/sample-matrix-field-setup/${id}`, {
          fields: selected.map((s) => ({
            fieldId: s.fieldId,
            fieldCaption: s.fieldCaption,
            fieldCustomCaption: s.fieldCustomCaption,
            freeze: s.freeze,
            width: s.width,
            sortOrder: s.sortOrder,
          })),
        });
      },
      // Save → "Saved successfully." → return to the list (shared useSaveFlow
      // standard, CLAUDE.md rule 7). ALPACA's Sample Matrix Field Setup has only
      // Save + Cancel — no Save & Close (removed 2026-09-29, user request).
      onSaved: () => onSaved(),
    });
  };

  const recordName = `Sample Matrix Field Setup — ${name || "…"}`;

  // Formatter for each Selected column — shared by the filter popups AND the row
  // filter so the option list can never disagree with what's shown.
  const SEL_COLS = ["name", "freeze", "width", "order", "caption"] as const;
  const selText = (r: FieldRow, key: string): string => {
    switch (key) {
      case "name": return r.fieldCaption;
      case "freeze": return r.freeze ? "Yes" : "No";
      case "width": return String(r.width);
      case "order": return String(r.sortOrder);
      case "caption": return r.fieldCustomCaption ?? "";
      default: return "";
    }
  };
  const filteredAvailable = available.filter((a) =>
    passesColumnFilter(availFilters.fieldCaption ?? null, a.fieldCaption),
  );
  const filteredSelected = selected.filter((r) =>
    SEL_COLS.every((k) => passesColumnFilter(selFilters[k] ?? null, selText(r, k))),
  );

  // Keyboard shortcuts (standing rule 14) — mirror the Save / Cancel buttons.
  useHotkeys([
    { combo: "mod+s", label: "Save", group: "Sample Matrix Field Setup", order: 1, disabled: !canWrite, run: () => void doSave() },
    { combo: "esc", label: "Cancel", group: "Sample Matrix Field Setup", order: 2, allowInInput: false, run: () => onClose() },
  ]);

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div
            className="sd-head"
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}
          >
            <div style={{ minWidth: 0 }}>
              <FormTitle
                pageId={pageId}
                title={recordName}
                loadMs={loadMs}
                loading={loading}
                auditFormName="Sample Matrix Field Setup"
                auditSourceId={id ?? undefined}
              />
              <div className="sd-bc">
                {onHome && (
                  <>
                    <a onClick={onHome}>Home</a>
                    <i className="ti ti-chevron-right" />
                  </>
                )}
                {group && (
                  <>
                    <span>{group}</span>
                    <i className="ti ti-chevron-right" />
                  </>
                )}
                {title && (
                  <>
                    <a onClick={onClose}>{title}</a>
                    <i className="ti ti-chevron-right" />
                  </>
                )}
                <span>{recordName}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
              {canWrite && (
                <button className="ca-btn" onClick={openCopyFields} title="Copy this field configuration to other matrices">
                  <i className="ti ti-copy" /> Copy Fields
                </button>
              )}
              {canWrite && (
                <button className="ca-btn" onClick={() => void doSave()} title={`Save (${formatCombo("mod+s")})`}>
                  <i className="ti ti-device-floppy" /> Save
                </button>
              )}
              <button className="ca-btn" onClick={onClose} title={`Cancel (${formatCombo("esc")})`}>
                <i className="ti ti-x" /> Cancel
              </button>
            </div>
          </div>

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading">Loading…</div>
            ) : (
              <>
                {/* Header — read-only (this screen edits an existing matrix's field setup). */}
                <div className="sfc-header">
                  <label className="sfc-field">
                    <span className="sfc-label">Sample Matrix</span>
                    <input className="sd-input sfc-readonly" value={name} readOnly disabled />
                  </label>
                  <label className="sfc-field">
                    <span className="sfc-label">Matrix</span>
                    <input className="sd-input sfc-readonly" value={matrixName} readOnly disabled />
                  </label>
                </div>

                {/* Single "Field Configuration" tab above the Available/Selected
                    panels (ALPACA VisualMatrix_DetailView_FieldSetup — one tab, no
                    Station/Sampling/Monitoring split). */}
                <div className="sfc-tab-body">
                  <div className="sfc-tabs">
                    <button type="button" className="sfc-tab active">
                      Field Configuration
                    </button>
                  </div>
                  <div className="sfc-panels">
                    {/* Available */}
                    <div className="sfc-panel">
                      <div className="sfc-panel-head">
                        Available <span className="sfc-tab-count">{available.length}</span>
                      </div>
                      {/* FieldCaption column header + Excel-style filter popup
                          (ALPACA parity — the "FieldCaption" grid header with its
                          search + (All)/(Blanks)/(Non blanks) + value list). */}
                      <div className="sfc-avail-colhead">
                        <span>FieldCaption</span>
                        <ColFilterButton
                          variant="inline"
                          col="fieldCaption"
                          label="FieldCaption"
                          rows={available}
                          formatter={(a) => a.fieldCaption}
                          value={availFilters.fieldCaption ?? null}
                          onChange={(v) => setAvailFilters((p) => ({ ...p, fieldCaption: v }))}
                        />
                      </div>
                      <div className="sfc-avail">
                        {available.length === 0 && <div className="sfc-empty">No more fields</div>}
                        {available.length > 0 && filteredAvailable.length === 0 && (
                          <div className="sfc-empty">No fields match the filter.</div>
                        )}
                        {filteredAvailable.map((a) => (
                          <div
                            key={a.fieldId}
                            className={`sfc-avail-row${canWrite ? " sfc-draggable" : ""}`}
                            draggable={canWrite}
                            onDragStart={(e) => {
                              if (!canWrite) return;
                              dragIdRef.current = a.fieldId;
                              e.dataTransfer.setData("text/plain", a.fieldId);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragEnd={() => {
                              dragIdRef.current = null;
                            }}
                            title={canWrite ? "Drag into Selected, or click +" : undefined}
                          >
                            <span className="sfc-avail-name">{a.fieldCaption}</span>
                            {canWrite && (
                              <button className="sfc-add" title="Add" onClick={() => addField(a)}>
                                <i className="ti ti-plus" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selected — also a drop target for Available fields dragged in. */}
                    <div
                      className={`sfc-panel sfc-panel-selected${dropOver ? " sfc-drop-over" : ""}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (!dropOver) setDropOver(true);
                      }}
                      onDragLeave={(e) => {
                        // Only clear when the pointer actually leaves the panel.
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDropOver(false);
                        addFieldById(e.dataTransfer.getData("text/plain") || dragIdRef.current);
                      }}
                    >
                      <div className="sfc-panel-head">
                        Selected <span className="sfc-tab-count">{selected.length}</span>
                      </div>
                      <div className="sfc-sel-scroll">
                        <table className="sfc-sel-table">
                          <thead>
                            <tr>
                              <th></th>
                              {([
                                ["name", "Name"],
                                ["freeze", "Freeze"],
                                ["width", "Width"],
                                ["order", "Order"],
                                ["caption", "Caption"],
                              ] as const).map(([key, lbl]) => (
                                <th key={key}>
                                  <span className="sfc-th">
                                    {lbl}
                                    <ColFilterButton
                                      variant="inline"
                                      col={key}
                                      label={lbl}
                                      rows={cascadeRows(selected, selFilters, key, selText)}
                                      formatter={(r) => selText(r, key)}
                                      value={selFilters[key] ?? null}
                                      onChange={(v) => setSelFilters((p) => ({ ...p, [key]: v }))}
                                    />
                                  </span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {selected.length === 0 && (
                              <tr>
                                <td colSpan={6} className="sfc-empty">
                                  No fields selected — add from the left.
                                </td>
                              </tr>
                            )}
                            {selected.length > 0 && filteredSelected.length === 0 && (
                              <tr>
                                <td colSpan={6} className="sfc-empty">
                                  No fields match the filter.
                                </td>
                              </tr>
                            )}
                            {filteredSelected.map((r) => (
                              <tr key={r.fieldId}>
                                <td>
                                  {canWrite && (
                                    <button className="sfc-remove" title="Remove" onClick={() => removeField(r)}>
                                      <i className="ti ti-minus" />
                                    </button>
                                  )}
                                </td>
                                <td className="sfc-name-cell">{r.fieldCaption}</td>
                                <td className="sfc-cell-center">
                                  <input
                                    type="checkbox"
                                    checked={r.freeze}
                                    disabled={!canWrite}
                                    onChange={(e) => patchRow(r.fieldId, { freeze: e.target.checked })}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="sfc-cell-num"
                                    value={r.width}
                                    disabled={!canWrite}
                                    onChange={(e) => patchRow(r.fieldId, { width: Number(e.target.value) || 0 })}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="sfc-cell-num"
                                    value={r.sortOrder}
                                    disabled={!canWrite}
                                    onChange={(e) => patchRow(r.fieldId, { sortOrder: Number(e.target.value) || 0 })}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="sfc-cell-input"
                                    value={r.fieldCustomCaption ?? ""}
                                    disabled={!canWrite}
                                    onChange={(e) => patchRow(r.fieldId, { fieldCustomCaption: e.target.value })}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Copy Fields dialog (ALPACA "Copy Fields") — copy this matrix's field set to
          the checked target matrices, replacing their configuration. */}
      <AppDialog
        open={copyOpen}
        title="Sample Matrix Field Setup"
        icon="ti-copy"
        onClose={() => setCopyOpen(false)}
        footer={
          <>
            <button className="ca-btn" onClick={() => void doCopyFields()} disabled={copyBusy}>
              <i className="ti ti-check" /> OK
            </button>
            <button className="ca-btn" onClick={() => setCopyOpen(false)} disabled={copyBusy}>
              <i className="ti ti-x" /> Cancel
            </button>
          </>
        }
      >
        <div style={{ minWidth: 460 }}>
          {/* Copy Fields From — heading on top, then the Sample Matrix field (G5). */}
          <div className="sfc-panel-head" style={{ marginBottom: 8 }}>Copy Fields From</div>
          <div className="sfc-header" style={{ paddingBottom: 8 }}>
            <label className="sfc-field">
              <span className="sfc-label">Sample Matrix</span>
              <input className="sd-input sfc-readonly" value={name} readOnly disabled />
            </label>
          </div>
          <div className="sfc-panel-head" style={{ marginBottom: 6, marginTop: 12 }}>Copy Fields To</div>
          <div className="sfc-sel-scroll" style={{ maxHeight: 280 }}>
            <table className="sfc-sel-table">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      checked={copyTargets.length > 0 && copyChecked.size === copyTargets.length}
                      onChange={(e) =>
                        setCopyChecked(e.target.checked ? new Set(copyTargets.map((t) => t.oid)) : new Set())
                      }
                    />
                  </th>
                  <th>Sample Matrix</th>
                  <th>Matrix</th>
                </tr>
              </thead>
              <tbody>
                {copyTargets.length === 0 && (
                  <tr>
                    <td colSpan={3} className="sfc-empty">No other matrices.</td>
                  </tr>
                )}
                {copyTargets.map((t) => (
                  <tr key={t.oid} onClick={() => toggleCopyTarget(t.oid)} style={{ cursor: "pointer" }}>
                    <td className="sfc-cell-center">
                      <input
                        type="checkbox"
                        checked={copyChecked.has(t.oid)}
                        onChange={() => toggleCopyTarget(t.oid)}
                        // Stop the click bubbling to the row's onClick — otherwise the
                        // checkbox toggles via onChange AND the row toggles again, so
                        // it never changes (user report 2026-09-29 "can't select one").
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="sfc-name-cell">{t.sampleMatrix}</td>
                    <td>{t.matrix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AppDialog>
    </div>
  );
}
